// Årsskifte: indtægt, vækst, pakketilbud og start af næste år (eller finalen).

import { clockFull, darkCause, isPowered } from './cities';
import { addCrew } from './crews';
import { applyPack, packOffer, yearGrant, yearIncome } from './economy';
import { adoptYear, clearPlanned } from './events';
import type { LevelDef, PackId, PlayerId, SimEvent, State, YearSummary } from './types';

/** Luk året: udbetal resten af indtægten og tilbyd pakker. Starter næste år, hvis der ingen pakker er. */
export function endYear(state: State, level: LevelDef, events: SimEvent[]): void {
  const stats = yearSummary(state);
  const base = yearIncome(state);
  // Væksten og dens tilskud kom undervejs med vækstpulsen; her står de kun i årets tal.
  stats.grownMw = state.yearStats.grownMw;
  stats.grant = state.yearStats.grant;
  if (state.yearStats.repairKr) stats.repairKr = state.yearStats.repairKr;
  stats.baseGrant = yearGrant(state);
  stats.earlyPct = state.earlyBonusPct;
  const earned = Math.round((base * (100 + state.earlyBonusPct)) / 100);
  // Dryppene undervejs er allerede i kassen og bliver der, også hvis året endte lidt under skønnet.
  const kept = Math.max(earned, state.advancePaid);
  const income = kept + stats.grant;
  stats.earlyKr = earned - base;
  stats.powerKr = kept - stats.baseGrant - stats.earlyKr;
  // Dryppene undervejs er allerede i kassen; årsskiftet betaler resten.
  stats.advance = state.advancePaid;
  state.budget += Math.max(0, earned - state.advancePaid);
  for (const n of state.nodes) {
    if (!n.city) continue;
    n.city.happyThisYear = true;
    n.city.dimTicks = 0;
  }
  state.advancePaid = 0;
  state.cashCarryMilli = 0;
  state.lastIncome = income;
  state.earlyBonusPct = 0;
  clearPlanned(state);
  state.yearStats = { servedSum: 0, greenSum: 0, exportSum: 0, ticks: 0, blackouts: 0, grownMw: 0, grant: 0 };
  state.packOffer = packOffer(state, level);
  const seen = (state.packsSeen ??= []);
  for (const p of state.packOffer) if (!seen.includes(p)) seen.push(p);
  events.push({ t: 'year_end', tick: state.tick, year: state.year, income, offer: [...state.packOffer], stats });
  state.phase = 'yearend';
  state.phaseTicks = 0;
  if (state.packOffer.length === 0) beginYear(state, level, events);
}

/** Nåde-tid (ticks) for hver by, når et tabt år tages om: 15 s til at nå de nye valg. */
export const RETRY_GRACE_TICKS = 150;

/**
 * Et tabt år taget om må ikke være en blindgyde. Playtest runde 3: checkpointet ved årets start kunne
 * have mørke byer med løbende ure og ét hjerte, så "Prøv året igen" var tabt før første tryk. Urene
 * nulstilles, hver by får 15 s nåde, og tilliden hæves til mindst 2 (højst sværhedens start).
 */
export function retryMercy(state: State, startTrust: number): void {
  for (const n of state.nodes) {
    const c = n.city;
    if (!c) continue;
    c.clock = 0;
    delete c.climbing;
    c.graceTicks = Math.max(c.graceTicks, RETRY_GRACE_TICKS);
  }
  state.trust = Math.max(state.trust, Math.min(2, startTrust));
}

/** Kalenderåret for spilår `year` (0 er banens første år). */
export function calendarYear(level: Pick<LevelDef, 'year'>, year: number): number {
  return level.year + year;
}

/**
 * Så længe (ticks) skal en by have manglet strøm i året, før årsskiftet nævner den. Playtest runde 3:
 * med 3 s stod der TRAVLT ÅR næsten hvert år; 8 s er et rigtigt hul, ikke et blink.
 */
export const SHAKY_TICKS = 80;

/** Årets tal, før de nulstilles. */
export function yearSummary(state: State): YearSummary {
  const ys = state.yearStats;
  const ticks = Math.max(1, ys.ticks);
  let lit = 0;
  let cities = 0;
  const dark: YearSummary['dark'] = [];
  const shaky: string[] = [];
  for (const n of state.nodes) {
    // Datacentre tæller med (IGN runde 1: "3/3 med strøm", mens datacentret var mørkt).
    if (!n.city || !n.active || n.phantom || (n.kind !== 'city' && n.kind !== 'datacenter')) continue;
    // En by, der lige er dukket op og stadig har sin frist, er ikke årets fejl (playtest runde 3).
    if (n.city.graceTicks > 0 && n.city.servedMw <= 0) continue;
    cities++;
    if (n.city.servedMw > 0 && !n.city.out && isPowered(n)) {
      lit++;
      if (n.city.dimTicks >= SHAKY_TICKS) shaky.push(n.id);
    } else {
      const servedPct = n.city.out || n.city.demandMw <= 0 ? 0 : Math.min(99, Math.floor((n.city.servedMw * 100) / n.city.demandMw));
      dark.push({ node: n.id, cause: darkCause(state, n), servedPct });
    }
  }
  return {
    blackouts: ys.blackouts,
    servedMw: Math.round(ys.servedSum / ticks),
    greenPct: ys.servedSum > 0 ? Math.round((ys.greenSum * 100) / ys.servedSum) : 0,
    lit,
    cities,
    dark,
    shaky,
    grownMw: 0,
    grant: 0,
    advance: 0,
  };
}

/** Vælg en pakke (eller ingen) og start næste år. */
export function pickPack(
  state: State,
  level: LevelDef,
  index: number | 'both',
  auto: boolean,
  events: SimEvent[],
  by?: PlayerId | 'both',
  price?: number,
): void {
  const packs: PackId[] = index === 'both' ? [...state.packOffer] : state.packOffer[index] ? [state.packOffer[index] as PackId] : [];
  for (const pack of packs) {
    const from = events.length;
    if (!(state.packsTaken ??= []).includes(pack)) state.packsTaken.push(pack);
    applyPack(state, pack, () => addCrew(state, 'van', level.crewHome, events), events, auto);
    for (let i = from; i < events.length; i++) {
      const e = events[i];
      if (e?.t !== 'pack_picked') continue;
      if (by) e.by = by;
      if (price !== undefined) e.both = price;
    }
  }
  delete state.packVotes;
  state.lastOffer = state.packOffer;
  state.packOffer = [];
  beginYear(state, level, events);
}

/** Nytår: intet ur står over denne andel (promille) af fuldt, når det nye år starter. */
export const NEW_YEAR_CLOCK_PM = 500;
/** Nytår: en by, der mangler strøm, får 3 s frist, før uret løber igen. */
export const NEW_YEAR_GRACE_TICKS = 30;

/**
 * Nytårsfrist (spor "sværhed"). Urene står stille, mens gaven vælges (step kører ikke urene i
 * årsskiftet), og når året starter, falder hvert ur til højst det halve, og en by uden strøm får 3 s.
 * IGN runde 5: Holstebros ur stod på 975 ‰ ved årsskiftet og gik i sort 3 ticks efter gavevalget, så
 * gaven var det sidste, man nåede. Et halvt ur er stadig et ur: det haster, men man kan nå det.
 * Returnerer byerne, hvis ur blev sat ned.
 */
export function newYearMercy(state: State): string[] {
  const cap = Math.floor((clockFull(state) * NEW_YEAR_CLOCK_PM) / 1000);
  const eased: string[] = [];
  for (const n of state.nodes) {
    const c = n.city;
    if (!c || !n.active || c.darkTicks > 0) continue;
    if (c.clock > cap) {
      c.clock = cap;
      if (!n.phantom) eased.push(n.id);
    }
    if (c.clock > 0 && !n.phantom && !isPowered(n)) c.graceTicks = Math.max(c.graceTicks, NEW_YEAR_GRACE_TICKS);
  }
  return eased;
}

/** Start næste år. Når year når level.years, er det finalen. */
export function beginYear(state: State, level: LevelDef, events: SimEvent[]): void {
  newYearMercy(state);
  state.year++;
  state.finale = state.year >= level.years;
  state.phase = 'playing';
  state.phaseTicks = 0;
  adoptYear(state, level, state.year, state.finale);
  events.push({ t: 'year_start', tick: state.tick, year: state.year, finale: state.finale });
  if (state.finale) events.push({ t: 'finale', tick: state.tick });
}
